// @flow
import $ from "jquery";
import { MnObject } from "backbone.marionette";
import CryptoJS from "crypto-js";
import Radio from "backbone.radio";

import {
    SET_MERCHANT_REF
} from "common/actions/common";
import {
    UnableToAuthenticateSamlUser,
    SamlError
} from "common/helpers/errors";
import {
    captureException
} from "extensions/sentry";
import { emitter } from "common/emitter";
import {
    merchant,
    authentication,
    users
} from "common/config/api";
import { pazienHistory } from "common/components/pazienHistory";
import { request } from "common/components/request";
import { showError, ensureErrorCallback } from "common/components/error";
import { store } from "common/store";
import _ from "extensions/underscore";
import * as sessionWarningDialog from "common/features/sessionWarning/sessionWarning";
import strings from "common/strings";
import { providers, providerUserManagementName, providerDisplayName } from "merchant/features/userManagement/sso_helper";

import { ForcePasswordChangeView } from "./forcePasswordChange";
import { LoginView } from "./login";
import { LoginWrapper } from "./loginWrapper";
import { NewUserView } from "./newUserView";
import { NoPermissions } from "./noAccountPermissions";
import { RequestAccessButton } from "./requestAccess";
import { ResetPasswordRequestView } from "./resetPasswordRequestView";
import { ResetPasswordView } from "./resetPasswordView";

type ValidationObjectType = {
    hasError: boolean,
    type: string,
    message: string
};

const validateLogin = function({ email = "", password = "" }): ValidationObjectType {
    const validation: ValidationObjectType = {
        hasError: false,
        type: "",
        message: ""
    };
    
    if (!email.trim().length) {
        validation.type = "email";
        validation.message = strings.login.errors.emailEmpty;
        validation.hasError = true;
    } else if (!password.trim().length) {
        validation.type = "password";
        validation.message = strings.login.errors.passEmpty;
        validation.hasError = true;
    }
    
    return validation;
};

const toBase64 = function(str: string): string {
    const words = CryptoJS.enc.Utf8.parse(str); // WordArray object
    return CryptoJS.enc.Base64.stringify(words);
};

const normalizeLoginError = function(error, email) {
    console.error(error);
    
    if (error.name === "SecurityError") {
        return "There was a browser error. Please try again or contact us at help@pazien.com";
    }
    
    let errorMessage = strings.preferences.security.errors.wrongPassword;
    
    if (error.response.status >= 500 && error.response.status < 600) {
        errorMessage = strings.common.errors.internal;
    } else if (error.response.parsed?.title?.toLowerCase() === "no such user") {
        errorMessage = strings.preferences.security.errors.wrongPassword;
    } else if (error.response.parsed?.title?.toLowerCase().indexOf("attempts") > -1) {
        errorMessage = strings.preferences.security.errors.lockedDueToFailedAttempts;
    } else if (error.response.parsed?.title === strings.preferences.security.errors.accountHasBeenLocked) {
        // Remove timeout from the querystring
        pazienHistory.unsetQueryStringParameters({
            param: "timeout",
            trigger: false
        });
        errorMessage = `${strings.preferences.security.errors.accountHasBeenLocked} <i class="far fa-question-circle pz-popover" data-toggle="popover" data-trigger="focus" title="Why was your account locked?" `
            + `data-content="${strings.preferences.security.errors.lockedExplanation}"></i>`;
    } else if (error.response.parsed?.title === "User account disabled") {
        errorMessage = `${"Your account is currently locked <i class=\"far fa-question-circle pz-popover\" data-toggle=\"popover\" data-trigger=\"focus\" title=\"Why was your account locked?\" "
        + "data-content=\""}${strings.preferences.security.errors.locked90DaysExplanation}"></i>`;
    }
    
    return errorMessage;
};
const normalizePasswordChangeError = function(error) {
    console.error(error);
    let errorMessage = "Unable to update your password. For assistance, please contact your team admin or Pazien directly at help@pazien.com";
    if (error.response.parsed?.title?.toLowerCase().indexOf("attempts") > -1) {
        errorMessage = "This account has been locked because of too many failed login attempts.";
    } else if (error.response.parsed?.title?.toLowerCase() === "wrong old password") {
        errorMessage = "Your old password didn't work. Let's try again";
    } else if (error.response.parsed?.description?.toLowerCase() === "already used") {
        errorMessage = "Your new password is not different from your last 6 passwords.";
    } else {
        captureException(new Error("Unknown password change error"), {
            extra: {
                request: {
                    duration: error.request.duration,
                    endTime: error.request.endTime,
                    startTime: error.request.startTime
                },
                status: error.response.status,
                statusText: error.response.statusText
            }
        });
    }
    
    return errorMessage;
};

export const LoginController = MnObject.extend({
    layout: false,
    channelName: "login",
    radioEvents: {
        changePassword: "changePassword",
        updatePassword: "updatePassword",
        authenticate: "authenticate"
    },
    
    radioRequests: {
        "merchant:switch": function(params = {}) {
            this.select_merchant({}, {
                fullPage: !!params.fullPage,
                success: params.success,
                error: params.error
            });
        },
        "merchant:demo": function(params = {}) {
            this.demo_merchant({}, {
                fullPage: !!params.fullPage,
                success: params.success,
                error: params.error,
                merchantID: params.merchantID,
                merchantName: params.merchantName,
                packageRef: params.packageRef
            });
        },
        "token:renew": "renewToken"
    },
    
    initialize() {
        // Listen to security events
        this.listenTo(Radio.channel("security"), "showSessionTimeout", this.showSessionTimeout);
        this.listenTo(Radio.channel("security"), "logoutComplete", this.logoutComplete);
        this.listenTo(Radio.channel("security"), "forcePasswordChange", this.forcePasswordChange);
        this.listenTo(Radio.channel("security"), "noViewPrivileges", this.noPermissions);
    },
    
    showSessionTimeout() {
        try {
            const tips = document.querySelectorAll("appcues-layer");
            if (tips.length && typeof tips.forEach === "function") {
                tips.forEach((e) => e.remove());
            }
        } catch (err) {
            captureException(err);
        }
        
        if (this.sessionWarningDialogShown) return;
        this.sessionWarningDialogShown = true;
        
        sessionWarningDialog.show({
            onSignout: ({ rememberLastUrl }) => this.logout({ rememberLastUrl }),
            onExtend: () => {
                this.sessionWarningDialogShown = false;
                this.renewToken();
                Radio.channel("security").trigger("sessionExtended");
            }
        });
    },
    
    toggleSidebarsForView(view) {
        const toggleSidebars = function(toggle) {
            $("#sidebar-wrapper").toggleClass("hidden", toggle);
            $("#topbar-wrapper").toggleClass("hidden", toggle);
        };
        
        this.listenTo(view, "destroy", () => {
            toggleSidebars(false);
            this.stopListening(view);
        });
        this.listenTo(view, "render", () => { toggleSidebars(true); });
    },
    
    login(opts: ControllerOptionsType, { timeout = false, state = {}, recheckAuthType = true } = {}) {
        const loginViewInstance = new LoginView({
            showTimeoutMessage: timeout === "true",
            state
        });
        
        // Get stored info for the user
        const storedAuthData = loginViewInstance.getStoredUserInfo();
        
        // If there is a username in stored info then check again if the user has been changed to different auth type
        if (recheckAuthType && storedAuthData.username) {
            loginViewInstance.getAuthInfo(storedAuthData).then(() => {
                loginViewInstance.setStoredAuthInfo();
            }).catch(() => {
                // There was an error syncing sso status with the API, but we still can go ahead and don't show the error
            }).finally((err) => {
                // At this point fresh auth info is recorded in the localStorage and we can show the view instance
                this._showView(loginViewInstance);
            });
        } else {
            Radio.channel("security").request("user:set:authInfo", null);
            
            // And then render the view
            this._showView(loginViewInstance);
        }
    },
    
    loginWithProvider(opts: ControllerOptionsType, providerID) {
        if (Object.prototype.hasOwnProperty.call(providers, providerID) || providerID === "pazien") {
            this.login({}, {
                state: {
                    step: 2,
                    authInfo: {
                        sso: providerID === "pazien" ? null : providerID
                    }
                },
                recheckAuthType: false
            });
        } else {
            pazienHistory.navigate("login", { replace: true, trigger: true });
        }
    },
    
    noPermissions() {
        request(merchant.admins).then((admins = []) => {
            this._showView(new LoginWrapper({ stretch: true, content: <NoPermissions admins={admins} /> }));
        });
    },
    
    forcePasswordChange(token, expiresAt, passExpiresInDays) {
        // Remove tooltips if any
        // $FlowFixMe We can't extend jquery type-def right now https://stackoverflow.com/questions/39445790/how-to-add-members-to-an-existing-definition-in-flowjs
        $(".tooltip").tooltip("destroy");
        
        // Get userName
        request(users.me, {
            ensureAuth: false,
            headers: {
                Authorization: token
            }
        }).then((user) => {
            const view = new ForcePasswordChangeView({
                token,
                passExpiresInDays,
                user
            });
            
            this._showView(view);
        });
    },
    
    /**
     * Update their passwords
     * @param {Object} data Object with newPassword and temp token
     */
    updatePassword(data) {
        let url;
        
        if (data.forgotMyPassword) {
            url = authentication.password.reset;
        } else {
            url = authentication.password.update;
        }
        
        return request(url, {
            ensureAuth: false,
            method: "PATCH",
            data: { new_password: data.newPassword },
            headers: {
                Authorization: data.token
            }
        }).then(() => {
            // Let the API know that a user is authenticating without a username, but with a token
            if (data.forgotMyPassword) {
                request("/auth/tokenValidate/passwordReset");
            } else {
                request("/auth/tokenValidate/newUser");
            }
        }).catch((error) => {
            data.onFailed(normalizePasswordChangeError(error));
        });
    },
    
    /**
     * Change user password from `old` to `new`
     * @param {Object} data
     * @param {String} data.oldPassword Previous password
     * @param {String} data.newPassword New password to set
     * @param {Function} data.onFailed function to call on fail
     */
    changePassword(data) {
        return request(authentication.password.update, {
            method: "PATCH",
            data: {
                new_password: data.newPassword,
                old_password: data.oldPassword
            }
        }).then(() => {
            data.onSuccess("<div>Success</div><small>For your security, your new password will expire in 90 days. "
                + "We'll send you a reminder when it comes time to update.</small>");
        }).catch((error) => {
            data.onFailed(normalizePasswordChangeError(error));
        });
    },
    
    renewToken: _.throttle(() => {
        if (Radio.channel("security").request("user:isLoggedIn")) {
            request("/auth/renew/").catch((err) => {
                // noop
            });
        }
    }, 10000),
    
    authenticate(data) {
        // Do some pre-checks
        const validation: ValidationObjectType = validateLogin(data);
        if (validation.hasError) {
            return data.onUnauthorized(validation);
        }
        return request(authentication.login, {
            ensureAuth: false,
            headers: {
                Authorization: `Basic ${toBase64([data.email, data.password].join(":"))}`
            }
        }).then(() => {
            if (typeof data.onAuthorized === "function") {
                data.onAuthorized();
            }
        }).catch((error) => {
            const errorMessage = normalizeLoginError(error, data.email);
            data.onUnauthorized(errorMessage);
        });
    },
    
    logout({ rememberLastUrl = false } = {}) {
        // Setting token to null immediately in case of network error
        Radio.channel("security").request("user:logout", { rememberLastUrl });

        // Logout on the API side
        request(authentication.logout, {
            ensureAuth: false,
            
            // API server needs token to mark it as inactive
            headers: {
                Authorization: Radio.channel("security").request("token:get")
            }
        });
    },
    
    logoutComplete({ logoutUrl }) {
        window.location.href = logoutUrl;
    },
    
    /**
     * We need to exchange token to a new one without browser being a context
     * @example: User requests password change in browser A, but opens email in browser B
     */
    exchangeResetTokenAndRenderView(View, params) {
        const self = this;
        
        request(authentication.password.reset, {
            ensureAuth: false,
            headers: {
                Authorization: params.token
            }
        }).then((data) => {
            self._showView(new View({
                token: data.token,
                expiresAt: params.expires_at
            }));
        }).catch(() => {
            // Something went wrong when exchanging the token. Most likely, the token user attempting to exchange is invalid or expired
            // Ask the user to submit another request to reset by passing current Date and no token to the reset pass view
            self._showView(new View({
                token: 0,
                expiresAt: +new Date()
            }));
        });
    },
    
    _showView(view) {
        // Subscribe to different view events to show/hide page sidebars
        this.toggleSidebarsForView(view);
        
        // Append login view to the content region.
        // showing the view will also hide the sidebars.
        const layout = Radio.channel("app").request("get:layout");
        layout.getRegion("topBar").empty();
        layout.getRegion("leftSidebar").empty();
        layout.showChildView("content", view);
    },
    
    new_user(options: ControllerOptionsType, params) {
        if (params["password_token"]) {
            this.exchangeResetTokenAndRenderView(NewUserView, {
                token: params["password_token"],
                expires_at: params["expires_at"]
            });
        } else {
            // Just go to the login page w/o throwing error
            Radio.channel("security").request("system:force:login:page");
        }
    },
    
    saml_user(options: ControllerOptionsType, { token, merchant: merchant_ref }) {
        let postError: string = "";
        if (token) {
            if (merchant_ref) {
                store.dispatch(SET_MERCHANT_REF(merchant_ref));
            }
            
            const authenticate = Radio.channel("security").request("user:authenticate", token, void 0, true);
            if (authenticate === false) {
                postError = "Unable to authenticate";
            }
        } else {
            postError = "Missing token";
            showError(new UnableToAuthenticateSamlUser({
                message: "Unable to authenticate SAML user"
            }));
        }

        if (postError) {
            emitter.emit("tracking.track", {
                type: "samlError",
                data: {
                    message: postError
                }
            });
        }
    },
    
    async saml_error(options: ControllerOptionsType, params: {| username: string, error: number |}) {
        let userData = [null, null];
        
        if (params.username) {
            try {
                userData = await request("/user/sso", {
                    data: { username: params.username },
                    ensureAuth: false
                });
            } catch {
                // noop
            }
        }
        
        showError(new SamlError({
            error: params.error,
            displayName: providerDisplayName(userData.sso),
            userManagementName: providerUserManagementName(userData.sso),
            custom: true,
            actions: false,
            overrideUsername: params.username,
            info: params.error === "4" ? <RequestAccessButton username={params.username} /> : void 0,
            hideDetails: params.error === "4",
            extra: {
                username: params.username
            }
        }));
    },
    
    reset(options: ControllerOptionsType, { reset_password_token: token, email, expires_at } = {}) {
        if (token) {
            this.exchangeResetTokenAndRenderView(ResetPasswordView, { token, expires_at });
        } else {
            // It's a submission to receive a link to reset the password
            this._showView(new ResetPasswordRequestView({ email }));
        }
    },
    
    async select_merchant(options: ControllerOptionsType, params) {
        try {
            const { merchantPickerViewFactory } = await import("merchant/features/merchantPickerView/merchantPicker");
            const view = merchantPickerViewFactory(params);
            if (params.fullPage) {
                this._showView(view);
            } else {
                Radio.channel("main").request("ensure:background:content");
            }
        } catch (err) {
            ensureErrorCallback(err);
        }
    },
    
    async demo_merchant(options: ControllerOptionsType, params) {
        try {
            const { merchantDemoPickerFactory } = await import("merchant/features/merchantPickerView/merchantDemoPicker");
            const view = merchantDemoPickerFactory(params);
            
            if (params.fullPage) {
                this._showView(view);
            } else {
                Radio.channel("main").request("ensure:background:content");
            }
        } catch (err) {
            ensureErrorCallback(err);
        }
    }
});
